import React from 'react';
import clsx from 'clsx';
import { Pencil, Trash2 } from 'lucide-react';
import { META_UNILEVER_COLUMNS, MetaUnileverColumn, MetaUnileverRow } from './types';
import { finalizeMetaValue, formatCurrencyBRL, sanitizeMetaInput } from './utils';

type MetasUnileverTableProps = {
  rows: MetaUnileverRow[];
  changedGuids: Set<string>;
  onChange: (guid: string, key: keyof Omit<MetaUnileverRow, 'guid'>, value: string) => void;
  onRemove: (guid: string) => void;
};

type EditableCellProps = {
  row: MetaUnileverRow;
  column: MetaUnileverColumn;
  isChanged: boolean;
  onChange: MetasUnileverTableProps['onChange'];
};

const EditableCell = React.memo<EditableCellProps>(({
  row,
  column,
  isChanged,
  onChange,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const rawValue = row[column.key];
  const isMoneyCell = column.type === 'money';
  const value = isMoneyCell && !isFocused
    ? formatCurrencyBRL(rawValue)
    : String(rawValue ?? '');

  const handleChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = isMoneyCell
      ? sanitizeMetaInput(event.target.value)
      : event.target.value;
    onChange(row.guid, column.key, nextValue);
  }, [column.key, isMoneyCell, onChange, row.guid]);

  const handleBlur = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    if (!isMoneyCell) return;

    onChange(row.guid, column.key, finalizeMetaValue(event.target.value));
    setIsFocused(false);
  }, [column.key, isMoneyCell, onChange, row.guid]);

  const handleFocus = React.useCallback(() => {
    if (isMoneyCell) setIsFocused(true);
  }, [isMoneyCell]);

  return (
    <td className="px-4 py-3 align-middle">
      <input
        type="text"
        inputMode={isMoneyCell ? 'decimal' : 'text'}
        value={value}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          if (isMoneyCell && (event.key === ',' || event.keyCode === 188)) {
            event.preventDefault();
          }
        }}
        placeholder={isMoneyCell ? '0.00' : '-'}
        className={clsx(
          'h-9 w-full rounded-lg border px-3 text-sm text-gray-800 outline-none transition-all focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20',
          isChanged ? 'border-blue-200 bg-white' : 'border-transparent bg-transparent hover:border-gray-200 hover:bg-white',
          isMoneyCell && 'text-right font-mono'
        )}
        aria-label={`${column.label} de ${row.distribuidor || 'nova linha'}`}
      />
    </td>
  );
});

EditableCell.displayName = 'EditableCell';

type TableRowProps = {
  row: MetaUnileverRow;
  isChanged: boolean;
  onChange: MetasUnileverTableProps['onChange'];
  onRemove: MetasUnileverTableProps['onRemove'];
};

const TableRow = React.memo<TableRowProps>(({
  row,
  isChanged,
  onChange,
  onRemove,
}) => {
  const handleRemove = React.useCallback(() => {
    onRemove(row.guid);
  }, [onRemove, row.guid]);

  return (
    <tr
      className={clsx(
        'transition-colors',
        isChanged ? 'bg-blue-50/60 hover:bg-blue-50' : 'hover:bg-gray-50'
      )}
    >
      {META_UNILEVER_COLUMNS.map((column) => (
        <EditableCell
          key={column.key}
          row={row}
          column={column}
          isChanged={isChanged}
          onChange={onChange}
        />
      ))}
      <td className="px-4 py-3 text-right align-middle">
        <button
          type="button"
          onClick={handleRemove}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
          title="Remover linha"
          aria-label="Remover linha"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
});

TableRow.displayName = 'TableRow';

const MetasUnileverTableComponent: React.FC<MetasUnileverTableProps> = ({
  rows,
  changedGuids,
  onChange,
  onRemove,
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-[1120px] w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            {META_UNILEVER_COLUMNS.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={clsx(
                  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500',
                  column.type === 'money' && 'text-right'
                )}
              >
                <div className={clsx('flex items-center gap-1.5', column.type === 'money' && 'justify-end')}>
                  <Pencil className="h-3 w-3 text-blue-500" />
                  {column.label}
                </div>
              </th>
            ))}
            <th scope="col" className="w-14 px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
              Acoes
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((row) => (
            <TableRow
              key={row.guid}
              row={row}
              isChanged={changedGuids.has(row.guid)}
              onChange={onChange}
              onRemove={onRemove}
            />
          ))}
        </tbody>
      </table>

      {rows.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2 py-16">
          <div className="rounded-full bg-gray-100 p-4">
            <Pencil className="h-7 w-7 text-gray-400" />
          </div>
          <p className="text-sm text-gray-500">Nenhuma meta cadastrada</p>
        </div>
      )}
    </div>
  );
};

export const MetasUnileverTable = React.memo(MetasUnileverTableComponent);
